// SPDX-License-Identifier: AGPL-3.0-or-later
// Domain jargon, defined for someone who has never encountered any of it.
// Rendered as click-to-open popovers via [data-term] spans.
export interface Term { term: string; definition: string }

export const GLOSSARY: Record<string, Term> = {
  branch: {
    term: 'Branch',
    definition:
      'A bank premises staffed to give a full “branch level of service” — you can open accounts, deposit and withdraw cash, get help in person. In APRA’s data this is the "Branch" service-channel type. It is the gold standard of physical banking access, and the channel that has fallen the fastest.',
  },
  bankpost: {
    term: 'Bank@Post',
    definition:
      'A service run by Australia Post: at a participating post office you can deposit and withdraw cash and check balances for more than 80 banks, using your existing card. It is not a bank branch — you can’t get advice or open most products — but in a town whose last branch has closed, it is often the only place left to do everyday banking over a counter. This site tracks it separately so you can see where it now carries a community on its own.',
  },
  other: {
    term: 'Other face-to-face',
    definition:
      'A staffed point that offers some in-person banking but less than a full branch — for example an agency, an in-store service point, or limited-hours access. APRA reports it separately from full branches.',
  },
  atm: {
    term: 'ATM',
    definition:
      'An automatic teller machine operated by a bank (an “ADI-operated” ATM). This site counts only bank-owned ATMs, not the independent machines in pubs and convenience stores, because APRA only collects the bank-owned ones. Bank-owned ATMs have fallen even faster than branches as the big banks retired their fleets.',
  },
  adi: {
    term: 'ADI (authorised deposit-taking institution)',
    definition:
      'The legal name for a bank, credit union or building society — an institution APRA authorises to take deposits from the public. Every point on this site belongs to an ADI, except Bank@Post, which is run by Australia Post on behalf of many ADIs.',
  },
  eftpos: {
    term: 'Why EFTPOS is excluded',
    definition:
      'APRA’s collection also counts EFTPOS terminals — the card machines at shop counters. There are about 700,000 of them, and they are a way to pay a merchant, not a way to do your own banking (you can’t open an account or deposit a cheque at one). To keep this site about access to banking services, EFTPOS is left out of every figure here.',
  },
  remoteness: {
    term: 'Remoteness area',
    definition:
      'An ABS classification of how far a place is from services and population, in five steps from Major Cities through Inner and Outer Regional to Remote and Very Remote Australia. It is the fairest way to ask whether people far from the cities are losing access faster than people in them.',
  },
  sa3: {
    term: 'SA3 region',
    definition:
      'Statistical Area Level 3 — an ABS geography of roughly 30,000 to 130,000 people, about the size of a large suburb cluster or a regional town and its surrounds. There are about 340 nationally. Coarse enough to be stable, fine enough to be recognisable.',
  },
  sa2: {
    term: 'SA2 / town',
    definition:
      'Statistical Area Level 2 — the ABS’s suburb-or-town scale, usually 3,000 to 25,000 people. It is fine enough to tell when a specific town has lost its last branch, which an SA3 (which lumps several towns together) can hide. The Banking Deserts view works at this scale.',
  },
  lostlast: {
    term: 'Lost its last branch',
    definition:
      'A town (SA2) that had at least one bank branch in 2017 and has none today. It does not mean the town has no banking at all — many still have a Bank@Post or an ATM — but it means there is no longer a staffed bank branch within it.',
  },
  perCapita: {
    term: 'Branches per 10,000 residents',
    definition:
      'The number of branches in a region divided by its resident population, times 10,000. Raw counts mostly measure how many people live somewhere; the rate is what makes a small country town comparable to a capital-city suburb. Population is the ABS Estimated Resident Population.',
  },
  desert: {
    term: 'Banking desert',
    definition:
      'An area whose residents have no ready access to face-to-face banking. There is no single official definition; here it is used for towns that have lost their last branch — and most sharply for the ones that have no Bank@Post either.',
  },
};

/** Wrap a term in an info-icon trigger. `key` must exist in GLOSSARY. */
export function gloss(key: string, label?: string): string {
  const t = GLOSSARY[key];
  if (!t) return label ?? key;
  return `<span class="glossary-link" data-term="${key}" tabindex="0" role="button" aria-label="Definition of ${t.term}">${label ?? t.term}<span class="gloss-icon" aria-hidden="true">i</span></span>`;
}

let popover: HTMLDivElement | null = null;
function ensurePopover(): HTMLDivElement {
  if (!popover) {
    popover = document.createElement('div');
    popover.className = 'glossary-popover';
    popover.setAttribute('role', 'dialog');
    document.body.appendChild(popover);
  }
  return popover;
}
export function hideGlossary(): void {
  popover?.classList.remove('visible');
}
function show(trigger: Element): void {
  const key = trigger.getAttribute('data-term') ?? '';
  const t = GLOSSARY[key];
  if (!t) return;
  const el = ensurePopover();
  el.innerHTML = `<h4></h4><p></p>`;
  (el.querySelector('h4') as HTMLElement).textContent = t.term;
  (el.querySelector('p') as HTMLElement).textContent = t.definition;
  el.classList.add('visible');
  const r = trigger.getBoundingClientRect();
  const pr = el.getBoundingClientRect();
  let left = r.left;
  let top = r.bottom + 8;
  if (left + pr.width > window.innerWidth - 12) left = window.innerWidth - pr.width - 12;
  if (top + pr.height > window.innerHeight - 12) top = r.top - pr.height - 8;
  el.style.left = `${Math.max(12, left)}px`;
  el.style.top = `${Math.max(12, top)}px`;
}
export function initGlossary(): void {
  document.addEventListener('click', (e) => {
    const trigger = (e.target as Element).closest('.glossary-link');
    if (trigger) { e.stopPropagation(); show(trigger); return; }
    if (!(e.target as Element).closest('.glossary-popover')) hideGlossary();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hideGlossary();
    if ((e.key === 'Enter' || e.key === ' ') && (e.target as Element)?.classList?.contains('glossary-link')) {
      e.preventDefault();
      show(e.target as Element);
    }
  });
  window.addEventListener('resize', hideGlossary);
}
